// Español — textos legales y de soporte.
export default (C) => ({
  ui: {
    header: "LEGAL Y SOPORTE",
    back: "VOLVER",
    nav: "Documentos legales",
    onThisPage: "EN ESTA PÁGINA",
    lastUpdated: "ÚLTIMA ACTUALIZACIÓN",
    effective: "29 de octubre de 2026",
    companyDetails: "DATOS DE LA EMPRESA",
    cd: { company: "Empresa", nipt: "NIPT / Número fiscal", office: "Domicilio social y punto de servicio", email: "Correo electrónico", phone: "Teléfono" },
  },

  privacy: {
    kicker: "LEGAL · PRIVACIDAD",
    title: "Política de privacidad",
    intro: "Cómo Eagle Inc trata tus datos personales — en nuestros sitios web, en nuestros servicios y cuando nos pagas con tarjeta, Apple Pay, Google Pay, enlace de pago o código QR.",
    sections: [
      {
        id: "who-we-are",
        title: "Quiénes somos",
        body: [
          `Esta política de privacidad explica cómo ${C.legalName} ("Eagle Inc", "nosotros") recopila, utiliza y protege los datos personales cuando visitas cualquiera de nuestros sitios web, te pones en contacto con nosotros, recibes una factura o nos pagas con tarjeta, Apple Pay, Google Pay, enlace de pago o código QR.`,
          "Se aplica a todos los sitios web del grupo Eagle Inc, entre ellos eagle-inc.net, eagle-inc.al, eagle-inc.eu, eagle-eyes.al, eagle-commerce.al, digital-services.al, socialdeal.al, bookitnow.al e incontact.al, y a los servicios ofrecidos bajo las marcas Eagle Eyes (Real Estate, IT Management, Digital Services) y Eagle Commerce.",
          {
            kv: [
              ["Responsable del tratamiento", C.legalName],
              ["Nombres comerciales", C.tradeNames],
              ["NIPT / Número fiscal", C.nuis],
              ["Domicilio social", C.address],
              ["Contacto de privacidad", C.supportEmail],
              ["Teléfono", C.supportPhone],
            ],
          },
        ],
      },
      {
        id: "legal-framework",
        title: "Marco legal",
        body: [
          "Tratamos los datos personales de conformidad con la Ley albanesa n.º 124/2024 \"Sobre la protección de datos personales\". Si te encuentras en la Unión Europea o en el Espacio Económico Europeo y el Reglamento General de Protección de Datos (RGPD, Reglamento (UE) 2016/679) se aplica a nuestro tratamiento, también cumplimos el RGPD.",
          "La autoridad de control en Albania es el Comisionado para el Derecho a la Información y la Protección de Datos Personales (Komisioneri për të Drejtën e Informimit dhe Mbrojtjen e të Dhënave Personale), https://idp.al.",
        ],
      },
      {
        id: "data-we-collect",
        title: "Datos personales que recopilamos",
        body: [
          "Solo recopilamos los datos que necesitamos para los fines descritos a continuación:",
          {
            list: [
              "Datos de identidad y contacto — nombre, nombre de la empresa, correo electrónico, número de teléfono y dirección postal o de facturación, que nos facilitas a través de nuestros formularios de contacto y soporte, por correo electrónico, teléfono o WhatsApp, o cuando preparamos un presupuesto, contrato o factura.",
              "Datos contractuales y de facturación — los servicios contratados, presupuestos, contratos, facturas, números de factura, importes, moneda, datos de IVA y la correspondencia relativa al encargo.",
              "Datos de transacciones de pago — estado del pago, fecha, importe, moneda, tipo de medio de pago (p. ej., Visa, Mastercard, Apple Pay, Google Pay), una referencia de autorización o de transacción y, si el banco la facilita, un número de tarjeta enmascarado (p. ej., los cuatro últimos dígitos). Nunca recibimos ni almacenamos números de tarjeta completos, fechas de caducidad ni códigos CVV/CVC.",
              "Datos de soporte — el contenido de tus solicitudes de soporte y de reembolso y cualquier información que decidas compartir con nosotros cuando te ayudamos.",
              "Datos técnicos — dirección IP, tipo de navegador, información del dispositivo y registros de solicitudes que nuestra infraestructura de alojamiento registra automáticamente por seguridad y para servir el sitio web.",
            ],
          },
          "No recopilamos conscientemente categorías especiales de datos personales y te pedimos que no nos envíes este tipo de datos.",
        ],
      },
      {
        id: "card-payments",
        title: "Pagos con tarjeta, Apple Pay y Google Pay",
        body: [
          "Los pagos con tarjeta los procesa nuestro banco adquirente, Raiffeisen Bank Sh.A. (Albania), a través de su servicio de comercio electrónico RaiAccept. Cuando pagas mediante un enlace de pago o un código QR de RaiAccept, introduces los datos de tu tarjeta — o autorizas Apple Pay o Google Pay — en la página de pago segura del banco, no en nuestro sitio web.",
          {
            list: [
              "Los datos de tu tarjeta se transmiten cifrados (TLS) directamente al banco y a su infraestructura de pago conforme a PCI DSS. Eagle Inc no ve, no trata ni almacena el número completo de tu tarjeta ni el código de seguridad.",
              "Los pagos pueden ser verificados por el emisor de tu tarjeta con 3-D Secure (p. ej., Visa Secure, Mastercard Identity Check).",
              "Si utilizas Apple Pay o Google Pay, Apple o Google tratan tus datos según sus propias políticas de privacidad; el banco recibe un número de tarjeta tokenizado, no tu número de tarjeta real.",
              "Raiffeisen Bank, las marcas de tarjetas (Visa, Mastercard) y el emisor de tu tarjeta actúan como responsables independientes de los datos que tratan para ejecutar, proteger y liquidar el pago y para prevenir el fraude.",
              "Del banco solo recibimos la información necesaria para asociar el pago a tu factura y gestionar reembolsos y disputas (ver \"Datos personales que recopilamos\").",
            ],
          },
        ],
      },
      {
        id: "purposes",
        title: "Por qué utilizamos tus datos y base jurídica",
        body: [
          {
            kv: [
              ["Responder a tus consultas", "Medidas precontractuales adoptadas a petición tuya; nuestro interés legítimo en responder a los mensajes."],
              ["Presupuestos, contratos y prestación de servicios", "Ejecución de un contrato contigo."],
              ["Facturación y cobro", "Ejecución de un contrato; obligación legal (normativa contable y fiscal)."],
              ["Reembolsos, contracargos y disputas de pago", "Ejecución de un contrato; obligación legal; interés legítimo en la defensa de reclamaciones."],
              ["Atención al cliente", "Ejecución de un contrato; interés legítimo en prestar un buen servicio."],
              ["Prevención del fraude y seguridad del sitio web", "Interés legítimo en proteger a nuestros clientes, nuestro negocio y nuestros sistemas; obligación legal cuando proceda."],
              ["Registros contables, fiscales y normativos", "Obligación legal."],
              ["Recordar tu idioma", "Interés legítimo en un sitio web funcional (guardado solo en tu propio navegador)."],
            ],
          },
          "No vendemos datos personales, no los utilizamos para decisiones automatizadas con efectos jurídicos o significativamente similares para ti y solo enviamos comunicaciones de marketing si las has solicitado. Puedes darte de baja en cualquier momento.",
        ],
      },
      {
        id: "recipients",
        title: "Con quién compartimos los datos",
        body: [
          "Solo compartimos datos personales cuando es necesario, con:",
          {
            list: [
              "Raiffeisen Bank Sh.A. (Albania) y las marcas de tarjetas y redes de pago que intervienen en el procesamiento de tu pago, y Apple o Google si utilizas su monedero.",
              "Microsoft (alojamiento en Microsoft Azure y servicios de correo y productividad de Microsoft 365) — como nuestro encargado del tratamiento.",
              "MongoDB Inc. (alojamiento de bases de datos MongoDB Atlas para los formularios enviados desde el sitio web) — como nuestro encargado del tratamiento.",
              "GitHub (alojamiento del código fuente y canal de compilación; allí no se almacenan datos de clientes).",
              "Nuestros contables, auditores y asesores jurídicos, sujetos a deber de confidencialidad.",
              "Autoridades públicas, tribunales y fuerzas de seguridad cuando estemos legalmente obligados.",
            ],
          },
          "Cada encargado del tratamiento está vinculado por un contrato escrito y solo puede utilizar tus datos siguiendo nuestras instrucciones.",
        ],
      },
      {
        id: "transfers",
        title: "Transferencias internacionales",
        body: [
          "Algunos de nuestros proveedores (como Microsoft y MongoDB) pueden tratar datos en la Unión Europea o en otros países fuera de Albania. Cuando los datos se transfieren a un país que no ofrece un nivel de protección adecuado, nos basamos en las garantías adecuadas previstas en la Ley n.º 124/2024 y, en su caso, en el RGPD — como las cláusulas contractuales tipo — y en los propios compromisos de seguridad de los proveedores.",
        ],
      },
      {
        id: "retention",
        title: "Cuánto tiempo conservamos los datos",
        body: [
          {
            list: [
              "Consultas y solicitudes de soporte que no dan lugar a un contrato: hasta 24 meses después del último contacto.",
              "Contratos, facturas, registros de pagos y contables: durante el plazo exigido por la legislación contable y fiscal albanesa.",
              "Datos necesarios para una disputa, un reembolso o un contracargo: hasta que el asunto esté totalmente resuelto y haya vencido cualquier plazo de prescripción aplicable.",
              "Datos de registro técnicos: por regla general, no más de 90 días, salvo que se necesiten para investigar un incidente de seguridad.",
            ],
          },
          "Transcurridos estos plazos, los datos se eliminan o se anonimizan.",
        ],
      },
      {
        id: "cookies",
        title: "Cookies y almacenamiento local",
        body: [
          "Nuestros sitios web no utilizan cookies publicitarias ni de seguimiento. Guardamos un único elemento en el almacenamiento local de tu navegador — el idioma de visualización que elijas — para que el sitio lo recuerde. Este elemento nunca sale de tu dispositivo y puedes eliminarlo en cualquier momento desde la configuración del navegador.",
          "Para mostrar nuestras tipografías, tu navegador carga fuentes de Google Fonts y Fontshare, que reciben tu dirección IP como parte de esa solicitud técnica. La página de pago de RaiAccept, gestionada por el banco, puede utilizar sus propias cookies estrictamente necesarias para la seguridad y la prevención del fraude.",
        ],
      },
      {
        id: "security",
        title: "Cómo protegemos tus datos",
        body: [
          "Todos nuestros sitios web se sirven exclusivamente a través de HTTPS. Los datos se almacenan en proveedores de nube de confianza, el acceso está restringido a personas autorizadas con autenticación multifactor y aplicamos el principio de mínimo privilegio. Los datos de las tarjetas nunca llegan a nuestros sistemas.",
          "Si se produce una violación de la seguridad de los datos personales que pueda suponer un riesgo para tus derechos, lo notificaremos al Comisionado en un plazo de 72 horas y te informaremos sin dilación indebida cuando el riesgo sea alto, tal como exige la ley.",
        ],
      },
      {
        id: "your-rights",
        title: "Tus derechos",
        body: [
          "En las condiciones previstas por la ley, tienes derecho a:",
          {
            list: [
              "ser informado y acceder a los datos personales que tenemos sobre ti;",
              "rectificar los datos inexactos y completar los incompletos;",
              "obtener la supresión de tus datos (\"derecho al olvido\");",
              "limitar el tratamiento de tus datos;",
              "recibir tus datos en un formato estructurado y legible por máquina (portabilidad de los datos);",
              "oponerte al tratamiento basado en nuestros intereses legítimos y, en cualquier momento, a la mercadotecnia directa;",
              "retirar el consentimiento que hayas dado, sin que ello afecte al tratamiento anterior.",
            ],
          },
          `Para ejercer tus derechos, escribe a ${C.supportEmail} o a ${C.legalName}, ${C.address}. Podemos pedirte que verifiques tu identidad y responderemos en los plazos legales (normalmente en el plazo de un mes).`,
          "También tienes derecho a presentar una reclamación ante el Comisionado para el Derecho a la Información y la Protección de Datos Personales (https://idp.al) o, en la UE/EEE, ante la autoridad de protección de datos de tu país de residencia.",
        ],
      },
      {
        id: "children",
        title: "Menores",
        body: ["Nuestros servicios están dirigidos a empresas y adultos. No recopilamos conscientemente datos personales de menores de 16 años."],
      },
      {
        id: "changes",
        title: "Cambios en esta política",
        body: [
          "Podemos actualizar esta política de privacidad de vez en cuando. La versión vigente está siempre disponible en /privacy, con la fecha de la última actualización indicada al principio de la página. Los cambios importantes se comunicarán por correo electrónico a los clientes existentes.",
        ],
      },
      {
        id: "contact",
        title: "Contacto",
        body: [
          { kv: [["Empresa", C.legalName], ["Dirección", C.address], ["Correo electrónico", C.supportEmail], ["Teléfono", C.supportPhone], ["Soporte", "/support"]] },
        ],
      },
    ],
  },

  terms: {
    kicker: "LEGAL · TÉRMINOS DE USO",
    title: "Términos de uso",
    intro: "Las condiciones que se aplican a nuestros sitios web, a nuestros servicios y a los pagos realizados a Eagle Inc con tarjeta, Apple Pay, Google Pay, enlace de pago o código QR — incluida nuestra política de cancelación y reembolso.",
    sections: [
      {
        id: "merchant",
        title: "Información del comercio",
        body: [
          "Estos Términos de uso regulan el uso de los sitios web del grupo Eagle Inc y la compra y el pago de nuestros servicios. El comercio y vendedor para todos los pagos es:",
          {
            kv: [
              ["Comercio", C.legalName],
              ["Nombres comerciales", C.tradeNames],
              ["NIPT / Número fiscal", C.nuis],
              ["Domicilio social", C.address],
              ["País", "Albania"],
              ["Atención al cliente", `${C.supportEmail} · ${C.supportPhone}`],
              ["Página de soporte", C.supportUrl],
            ],
          },
          "Al utilizar nuestros sitios web, aceptar un presupuesto o pagar una factura, un enlace de pago o un código QR emitidos por nosotros, aceptas estos Términos. Si no estás de acuerdo, no utilices nuestros sitios web ni nuestros servicios.",
        ],
      },
      {
        id: "services",
        title: "Nuestros servicios",
        body: [
          "Eagle Inc ofrece servicios profesionales bajo sus marcas, entre ellos gestión de TI y consultoría de TI (nube, infraestructura, Microsoft 365/Azure, gestión de portafolio), servicios digitales y productos de software, y servicios inmobiliarios residenciales en Tirana.",
          "Nuestros servicios son intangibles: no hay envío físico. El alcance exacto, los entregables, los criterios de aceptación, el calendario y el precio de cada encargo se establecen en el presupuesto, la propuesta, el contrato o la factura que te emitimos por escrito (el \"Pedido\"). Si un Pedido contradice estos Términos, prevalece el Pedido.",
        ],
      },
      {
        id: "orders",
        title: "Presupuestos, pedidos y contrato",
        body: [
          {
            list: [
              "El contrato se perfecciona cuando aceptas nuestro presupuesto o propuesta por escrito (por correo electrónico, mediante firma o pagando la factura o el enlace de pago correspondiente).",
              "Los presupuestos son válidos durante 30 días, salvo que se indique otra cosa.",
              "Los servicios y soluciones puntuales se facturan al inicio del encargo, salvo que el Pedido disponga otra cosa.",
              "Los servicios continuos (como TI gestionada y gestión de portafolio) se prestan mediante un contrato anual con facturación mensual, según lo establecido en el Pedido.",
              "Eres responsable de facilitar información correcta, acceso oportuno a los sistemas y la colaboración que razonablemente necesitemos para prestar el servicio.",
            ],
          },
        ],
      },
      {
        id: "prices",
        title: "Precios y moneda",
        body: [
          "Los precios figuran en el Pedido y en la factura o el enlace de pago, en lek albaneses (ALL) o euros (EUR). El importe y la moneda que aparecen en la página de pago de RaiAccept son el importe y la moneda que se te cargarán.",
          "Salvo indicación expresa en contrario, los precios no incluyen el IVA, que se añade cuando corresponda según la legislación fiscal albanesa. Si tu tarjeta está en otra moneda, su emisor puede aplicar su propio tipo de cambio y sus comisiones; Eagle Inc no tiene control sobre ellos.",
        ],
      },
      {
        id: "payment",
        title: "Medios de pago y seguridad de los pagos",
        body: [
          "Aceptamos los siguientes medios de pago:",
          {
            list: [
              "tarjetas de crédito y débito Visa y Mastercard;",
              "Apple Pay y Google Pay, si tu dispositivo y tu tarjeta lo admiten;",
              "enlaces de pago y códigos QR generados a través de RaiAccept e incluidos en nuestras facturas o enviados por correo electrónico o mensaje;",
              "transferencia bancaria a la cuenta indicada en la factura.",
            ],
          },
          "Los pagos con tarjeta, Apple Pay y Google Pay los procesa nuestro banco adquirente, Raiffeisen Bank Sh.A. (Albania), a través de su servicio de comercio electrónico RaiAccept. Los datos de la tarjeta se introducen únicamente en la página de pago segura del banco, conforme a PCI DSS, mediante una conexión cifrada (TLS). Eagle Inc nunca ve ni almacena el número completo de tu tarjeta, la fecha de caducidad ni el código de seguridad. Los pagos pueden requerir la autenticación 3-D Secure de tu emisor.",
          "Cada enlace de pago o código QR está vinculado a una factura y un importe concretos. Antes de pagar, comprueba siempre que el nombre del comercio que aparece en la página de pago es «Eagle Inc». Si un enlace ha caducado o no funciona, ponte en contacto con soporte y te enviaremos uno nuevo.",
          "El pago vence en la fecha indicada en la factura. Podemos suspender los servicios continuos si una factura sigue impagada tras un recordatorio por escrito.",
          { note: `Eagle Inc nunca te pedirá que envíes el número completo de tu tarjeta, la fecha de caducidad ni el código de seguridad por correo electrónico, teléfono, chat ni ningún otro canal. Si recibes una solicitud así en nuestro nombre, no respondas y comunícalo a ${C.supportEmail}.` },
        ],
      },
      {
        id: "delivery",
        title: "Prestación de los servicios",
        body: [
          "Los servicios se prestan a distancia y/o de forma presencial, según se describa en el Pedido. La prestación comienza en la fecha de inicio acordada o tras la recepción del pago cuando se exija pago por adelantado. Los entregables (como informes, configuraciones, documentación o acceso a software) se entregan por vía electrónica o presencialmente. Recibirás una confirmación por correo electrónico cuando se haya prestado un servicio pagado o se haya completado un hito.",
        ],
      },
      {
        id: "cancellation",
        title: "Política de cancelación y reembolso",
        body: [
          {
            list: [
              "Antes del inicio del trabajo: puedes cancelar un Pedido por escrito en cualquier momento antes de que empecemos a prestar el servicio y recibir el reembolso íntegro de lo pagado.",
              "Después del inicio del trabajo: puedes cancelar por escrito; pagas el trabajo realizado y los costes incurridos hasta la fecha de cancelación, y se reembolsa cualquier importe anticipado por trabajo aún no realizado.",
              "Los contratos anuales con facturación mensual pueden resolverse según lo previsto en el contrato. Los meses ya facturados y prestados no son reembolsables, salvo que el servicio no se haya prestado.",
              "Cargos duplicados o incorrectos: si se te ha cobrado dos veces o un importe incorrecto, reembolsamos íntegramente el exceso.",
              "Servicios no prestados según lo acordado: si no prestamos un servicio conforme al Pedido y no podemos subsanarlo en un plazo razonable, tienes derecho a un reembolso total o proporcional.",
            ],
          },
          `Cómo solicitar un reembolso: escribe a ${C.supportEmail} o utiliza el formulario de /support, indicando número de factura, fecha de pago, importe y motivo. Confirmamos la recepción en 1 día hábil y decidimos en 5 días hábiles.`,
          "Los reembolsos aprobados se realizan en la misma tarjeta o medio de pago utilizado para el pago original (incluidos Apple Pay o Google Pay) — no reembolsamos pagos con tarjeta en efectivo ni a otra tarjeta. Iniciamos el reembolso en un plazo de 5 días hábiles desde la aprobación; según el emisor de tu tarjeta, pueden pasar hasta 10 días hábiles hasta que el importe aparezca en tu extracto.",
          "Si actúas como consumidor, las normas imperativas de protección de los consumidores del derecho albanés, incluido cualquier derecho legal de desistimiento en los contratos a distancia, se aplican además de esta política y no quedan limitadas por ella.",
        ],
      },
      {
        id: "disputes",
        title: "Disputas de pago y contracargos",
        body: [
          `Si no reconoces un cargo de Eagle Inc o no estás de acuerdo con un importe, ponte primero en contacto con nosotros en ${C.supportEmail} — la mayoría de los casos se resuelven rápidamente y se reembolsan directamente. Esto no afecta a tu derecho a impugnar una transacción ante el emisor de tu tarjeta. Si se inicia un contracargo, colaboraremos con el banco y aportaremos el Pedido, la factura y la documentación de prestación correspondientes.`,
        ],
      },
      {
        id: "website-use",
        title: "Uso de nuestros sitios web",
        body: [
          "Solo puedes utilizar nuestros sitios web con fines lícitos. No puedes intentar acceder sin autorización a nuestros sistemas, interferir en el funcionamiento de los sitios web, facilitar información falsa ni hacer un uso indebido de nuestros formularios. Podemos modificar, suspender o interrumpir cualquier parte de los sitios web en cualquier momento.",
          "Todo el contenido de nuestros sitios web — incluidos textos, logotipos, el medallón de Eagle, diseños, imágenes y software — es propiedad de Eagle Inc o se utiliza bajo licencia y está protegido por la normativa de propiedad intelectual. No puedes copiarlo ni reutilizarlo sin nuestro permiso previo por escrito.",
          "La información de nuestros sitios web es general y puede cambiar. No constituye una oferta salvo que se confirme en un Pedido. Los enlaces a sitios de terceros se ofrecen por comodidad; no somos responsables de su contenido.",
        ],
      },
      {
        id: "liability",
        title: "Responsabilidad",
        body: [
          "Prestamos nuestros servicios con la diligencia debida y conforme a las buenas prácticas profesionales. En la medida en que lo permita la ley, nuestra responsabilidad total derivada de un Pedido se limita al importe que hayas pagado en virtud de dicho Pedido en los doce meses anteriores al hecho que dé lugar a la reclamación, y no respondemos de daños indirectos o consecuentes, incluido el lucro cesante o la pérdida de datos.",
          "Nada de lo dispuesto en estos Términos excluye ni limita la responsabilidad que no pueda excluirse o limitarse conforme a la ley aplicable, incluida la responsabilidad por dolo o negligencia grave, ni tus derechos imperativos como consumidor.",
        ],
      },
      {
        id: "privacy",
        title: "Privacidad",
        body: ["Tratamos los datos personales de acuerdo con nuestra Política de privacidad, disponible en /privacy."],
      },
      {
        id: "force-majeure",
        title: "Fuerza mayor",
        body: ["Ninguna de las partes es responsable del incumplimiento o retraso causado por hechos ajenos a su control razonable, como catástrofes naturales, cortes de suministro eléctrico o de red de proveedores externos, guerras, huelgas o actuaciones de las autoridades. Las obligaciones quedan suspendidas mientras dure dicho hecho."],
      },
      {
        id: "law",
        title: "Ley aplicable y controversias",
        body: [
          "Estos Términos y cada Pedido se rigen por la legislación de la República de Albania. Siempre intentamos resolver primero las reclamaciones de forma amistosa — ponte en contacto con soporte. Las controversias que no puedan resolverse amistosamente serán competencia del tribunal competente de Tirana (Albania), sin perjuicio del derecho imperativo del consumidor a acudir a los tribunales de su lugar de residencia.",
        ],
      },
      {
        id: "changes",
        title: "Cambios en estos Términos",
        body: [
          "Podemos modificar estos Términos de vez en cuando. A cada Pedido se le aplica la versión publicada en /termsofuse en la fecha del Pedido. La fecha de la última actualización figura al principio de esta página.",
        ],
      },
    ],
  },

  support: {
    kicker: "ATENCIÓN AL CLIENTE",
    title: "Soporte",
    intro: "Ayuda con pagos, facturas, reembolsos y nuestros servicios. Nuestro equipo de soporte responde personalmente a cada solicitud.",
    tocContact: "Contactar con atención al cliente",
    tocRequest: "Enviar una solicitud de soporte",
    email: "Correo electrónico",
    phone: "Teléfono",
    hoursLabel: "Horario de soporte",
    hours: "Lunes – viernes, 09:00 – 17:00 (CET/CEST, hora de Tirana). Primera respuesta en 1 día hábil.",
    security: "Por tu seguridad, nunca nos envíes el número completo de tu tarjeta, la fecha de caducidad ni el código de seguridad (CVV/CVC). Para identificar un pago bastan el número de factura, la fecha, el importe y los cuatro últimos dígitos de tu tarjeta.",
    requestTitle: "Enviar una solicitud de soporte",
    faqTitle: "PREGUNTAS FRECUENTES",
    topics: [
      "Problema de pago (rechazado, fallido, enlace o código QR caducado)",
      "Solicitud de reembolso",
      "Cargo duplicado o incorrecto",
      "Pregunta sobre una factura",
      "Pregunta sobre un servicio o pedido",
      "Privacidad / solicitud sobre datos personales",
      "Otro",
    ],
    form: {
      name: "Nombre completo",
      email: "Correo electrónico",
      reference: "Número de factura o referencia (opcional)",
      message: "¿Cómo podemos ayudarte? No incluyas nunca el número completo de tu tarjeta ni el código de seguridad.",
      consent: "Al enviar este formulario aceptas que tratemos tus datos para gestionar tu solicitud, tal como se describe en /privacy.",
      send: "ENVIAR SOLICITUD",
      sending: "ENVIANDO…",
      sentTitle: "Gracias — hemos recibido tu solicitud.",
      sentBody: (email) => `Responderemos en 1 día hábil a ${email}.`,
      error: `No hemos podido enviar tu mensaje. Escribe a ${C.supportEmail} o llama al ${C.supportPhone}.`,
    },
    faq: [
      {
        id: "pay-how",
        title: "¿Cómo pago una factura?",
        body: [
          "Cada factura incluye un enlace de pago seguro de RaiAccept y un código QR de Raiffeisen Bank Albania. Haz clic en el enlace o escanea el código QR con la cámara del móvil, comprueba el importe y que el comercio sea Eagle Inc, y paga con Visa, Mastercard, Apple Pay o Google Pay. También puedes pagar por transferencia bancaria a la cuenta indicada en la factura.",
        ],
      },
      {
        id: "pay-failed",
        title: "Mi pago ha sido rechazado o ha fallado",
        body: [
          {
            list: [
              "Comprueba que los datos de la tarjeta, la fecha de caducidad y los datos de facturación son correctos y que los pagos en línea/internacionales están habilitados en tu tarjeta.",
              "Completa la verificación 3-D Secure en la app de tu banco o con el código SMS — el pago falla si este paso caduca.",
              "Asegúrate de que la tarjeta tiene saldo o límite disponible suficiente.",
              "Prueba con Apple Pay o Google Pay, con otra tarjeta, o contacta con el emisor de tu tarjeta — los rechazos los decide el banco emisor, que puede indicarte el motivo.",
            ],
          },
          "Un pago rechazado no se cobra. Si tras un intento fallido aparece un importe retenido (pendiente) en tu cuenta, tu banco lo libera automáticamente, normalmente en pocos días hábiles.",
        ],
      },
      {
        id: "pay-link",
        title: "El enlace de pago o el código QR ha caducado o no se abre",
        body: ["Los enlaces de pago y los códigos QR tienen una validez limitada y son para un importe concreto. Escríbenos con tu número de factura y te enviaremos un nuevo enlace en 1 día hábil."],
      },
      {
        id: "double",
        title: "Me han cobrado dos veces o un importe incorrecto",
        body: ["Escríbenos con tu número de factura, la fecha y el importe de cada cargo y los cuatro últimos dígitos de tu tarjeta. Los cargos duplicados o incorrectos se reembolsan íntegramente en la tarjeta original."],
      },
      {
        id: "refund",
        title: "¿Cómo solicito un reembolso?",
        body: [
          `Utiliza el formulario de arriba o escribe a ${C.supportEmail} indicando número de factura, fecha de pago, importe y motivo de tu solicitud. Confirmamos la recepción en 1 día hábil y decidimos en 5 días hábiles. Los reembolsos aprobados se realizan en la misma tarjeta o monedero utilizado para el pago y se inician en 5 días hábiles; el emisor de tu tarjeta puede tardar hasta 10 días hábiles en mostrar el importe en tu extracto. La política completa está en nuestros Términos de uso: /termsofuse.`,
        ],
      },
      {
        id: "receipt",
        title: "¿Dónde está mi recibo o factura?",
        body: ["Tras un pago correcto recibes una confirmación de pago desde la página de pago. Enviamos la factura y, si lo solicitas, una confirmación de pago a la dirección de correo electrónico que tenemos registrada. Pide una copia a soporte si la necesitas."],
      },
      {
        id: "complaint",
        title: "Reclamaciones y escalado",
        body: [
          `Si no estás satisfecho con la gestión de tu solicitud, responde a nuestro mensaje pidiendo que se escale — la dirección de ${C.legalName} revisará tu reclamación y la responderá en un plazo de 10 días hábiles. Siempre conservas el derecho a dirigirte al emisor de tu tarjeta o a las autoridades competentes.`,
        ],
      },
    ],
  },
});
